/* ============================================================
   Notyfikator "agent chce się czegoś nauczyć" (panel-help-chat AI).

   Rola: gdy w `hotels/{id}/aiagents/build-state.dirtySections`
   pojawi się jakaś dirty sekcja (po zmianie w hotelu, eventach,
   produktach itp.), pokazujemy adminowi możliwość przebudowy bazy
   wiedzy agenta gościa. Komponent ma dwa warianty prezentacji:

   - `floating` (czat ZAMKNIĘTY) — przy prawej krawędzi obok awatara:
       • amber kropka z liczbą zmian na awatarze,
       • speech bubble „… chce się czegoś nauczyć" z ogonkiem do awatara,
       • półprzezroczysty OVERLAY pod bubble'em (modal-like, na pierwszym
         planie); klik w overlay chowa bubble na tę chwilę,
       • klik X → trwałe wyłączenie bubble'a (localStorage),
       • popover z checkboxami sekcji + „Aktualizuj zaznaczone".

   - `inline` (czat OTWARTY) — banner na górze szuflady czatu, żeby user
       mógł przebudować AI również z poziomu rozwiniętego czatu. Ignoruje
       trwałe wyciszenie bubble'a (X dotyczy tylko floating nag).

   Niewyświetla nic gdy: brak agenta AI / agent nieaktywny / brak dirty
   sekcji. W trybie floating dodatkowo respektuje wyciszenia admina.
   ============================================================ */
import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { AlertCircle, ChevronDown, Sparkles, X } from 'lucide-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { useHeroToast } from '@/hooks';
import { useOrganizationState } from '@/state';
import {
  getDirtySectionsList,
  KbSection,
  useGetAiAgentConfig,
  useKbBuildState,
  useRebuildKnowledgeBaseSections,
} from '@/views/AiAgents/queries';

import { KB_SECTION_META } from './stefanSectionMeta';

/* Wjazd bubble'a — zachowuje pionowe wyśrodkowanie (translateY(-50%)), bo
   bubble jest przyklejony przy prawej krawędzi obok awatara. */
const bounceIn = keyframes`
  from { opacity: 0; transform: translateY(calc(-50% + 8px)) scale(0.96); }
  to { opacity: 1; transform: translateY(-50%) scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pulseAnim = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
`;

/* Przyciemniające tło pod bubble'em — wybija notyfikację na pierwszy plan.
   z-index 999: pod awatarem (1000), bubble'em (1001) i kropką (1002), więc
   awatar + bubble zostają podświetlone, a reszta strony jest przygaszona. */
const NotifierOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgba(15, 23, 42, 0.32);
  cursor: pointer;
  animation: ${fadeIn} 200ms ease;
`;

/* Kropka na awatarze Stefana — amber (jak notyfikacje), subtelna pulsacja.
   Awatar tkwi przy prawej krawędzi, wyśrodkowany w pionie; kropkę kładziemy
   w jego prawym górnym rogu. */
const Dot = styled.span`
  position: fixed;
  right: 4px;
  top: calc(50% - 27px);
  z-index: 1002;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #f59e0b;
  border: 2px solid #ffffff;
  color: #ffffff;
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  animation: ${pulseAnim} 2.2s ease-in-out infinite;
`;

/* Speech bubble — styl czatu Stefana (białe tło, miękki cień, czarna treść).
   Ikona AlertCircle obok tytułu daje sygnał „coś wymaga uwagi" bez krzyku. */
const Bubble = styled.div`
  position: fixed;
  right: 90px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1001;
  max-width: min(300px, calc(100vw - 110px));
  padding: 12px 14px;
  padding-right: 32px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.14);
  font-size: 12.5px;
  line-height: 1.45;
  color: #0f172a;
  cursor: pointer;
  animation: ${bounceIn} 220ms ease-out;

  /* Ogonek po prawej stronie — wskazuje awatar przyklejony do krawędzi. */
  &::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    width: 12px;
    height: 12px;
    transform: translateY(-50%) rotate(45deg);
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }
`;

const BubbleClose = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }

  svg { width: 12px; height: 12px; }
`;

const BubbleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;

  svg {
    width: 16px;
    height: 16px;
    /* Amber — pasuje do kropki na FAB, sygnalizuje „uwaga" bez urgent. */
    color: #f59e0b;
    flex-shrink: 0;
  }
`;

const BubbleTitle = styled.div`
  font-weight: 300;
  color: #0f172a;
  font-size: 12.5px;
`;

const BubbleBody = styled.div`
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 2px;
`;

const PopoverAnchor = styled.div`
  position: fixed;
  right: 84px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 999;
  width: 1px;
  height: 1px;
  pointer-events: none;
`;

/* ---- Wariant inline (banner w rozwiniętym czacie) ---- */

const InlineBanner = styled.div`
  flex-shrink: 0;
  margin: 10px 10px 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(245, 158, 11, 0.35)' : '#fcd9a6')};
  background: ${({ theme }) => (theme.mode === 'dark'
    ? 'rgba(245, 158, 11, 0.10)'
    : 'linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)')};
`;

const InlineHeaderBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fde68a' : '#92400e')};

  .inline-ttl {
    flex: 1;
    min-width: 0;
    font-size: 12.5px;
    font-weight: 400;
    line-height: 1.3;
  }

  .inline-cnt {
    flex-shrink: 0;
    min-width: 18px;
    padding: 0 6px;
    border-radius: 999px;
    background: #f59e0b;
    color: #ffffff;
    font-size: 11px;
    font-weight: 400;
    line-height: 18px;
    text-align: center;
  }

  .inline-spark { color: #f59e0b; flex-shrink: 0; }
  .inline-chevron { color: currentColor; opacity: 0.7; flex-shrink: 0; }

  svg { width: 16px; height: 16px; }
`;

/* ---- Współdzielony panel przebudowy (popover content) ---- */

const PopoverInner = styled.div`
  width: 360px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PopoverTitle = styled.div`
  font-size: 14px;
  font-weight: 300;
  color: #0f172a;
`;

const PopoverHint = styled.div`
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.45;
  margin-top: 1px;
`;

const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

const SectionRow = styled.label`
  display: grid;
  grid-template-columns: auto 22px 1fr;
  gap: 10px;
  align-items: center;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 120ms ease;

  &:hover { background: #f1f5f9; }

  svg {
    width: 16px;
    height: 16px;
    color: #475569;
  }
`;

const SectionLabel = styled.span`
  font-size: 12.5px;
  font-weight: 500;
  color: #1f2937;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
`;

const STEFAN_BUBBLE_DISMISS_KEY = 'stefan-kb-bubble-dismissed';

interface StefanKbNotifierProps {
  /** `floating` (domyślnie) — bubble przy krawędzi gdy czat zamknięty.
      `inline` — banner w rozwiniętym czacie. */
  variant?: 'floating' | 'inline';
}

export const StefanKbNotifier: FC<StefanKbNotifierProps> = ({ variant = 'floating' }) => {
  const { t } = useTranslation();
  const { organizationId } = useOrganizationState();
  const { showSuccess, showError } = useHeroToast();
  const { data: agentConfig } = useGetAiAgentConfig(organizationId);
  const { data: buildState } = useKbBuildState(organizationId);
  const { mutateAsync: rebuild, isPending: isRebuilding } = useRebuildKnowledgeBaseSections();

  const dirtySections = useMemo(() => getDirtySectionsList(buildState), [buildState]);
  const hasDirty = dirtySections.length > 0;
  const guestAgentName = agentConfig?.name || t('stefan.defaultAgentName', 'Twój agent');
  const isInline = variant === 'inline';

  const [selected, setSelected] = useState<Set<KbSection>>(new Set());
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Trwałe wyciszenie bubble'a (klik X) — persystowane w localStorage,
  // przeżywa restart sesji. Dotyczy TYLKO floating nag, nie inline.
  const [bubbleClosedPermanently, setBubbleClosedPermanently] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STEFAN_BUBBLE_DISMISS_KEY) === '1';
  });
  // Tymczasowe schowanie bubble'a (klik w overlay / otwarcie popovera) —
  // resetuje się gdy pojawi się NOWA zmiana, żeby ponownie zaczepić admina.
  const [bubbleHidden, setBubbleHidden] = useState(false);

  // Sync zaznaczenia z dirty list (defaultem: wszystkie zaznaczone).
  useEffect(() => {
    setSelected(new Set(dirtySections));
  }, [dirtySections.length, dirtySections.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset tymczasowego schowania gdy pojawi się NOWA zmiana.
  useEffect(() => {
    if (hasDirty) setBubbleHidden(false);
  }, [dirtySections.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!organizationId) return null;
  // Pokaż notyfikator TYLKO gdy hotel ma skonfigurowanego agenta AI
  // i jest on aktywny. Bez agenta — rebuild bazy wiedzy nic nie da,
  // alert byłby tylko wizualnym szumem dla admina który nawet nie
  // używa AI w swoim hotelu.
  const hasActiveAgent = !!agentConfig && agentConfig.isActive !== false;
  if (!hasActiveAgent) return null;
  if (!hasDirty) return null;

  const dotLabel = dirtySections.length > 9 ? '9+' : String(dirtySections.length);

  const toggleSection = (section: KbSection) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  // Klik X — trwałe wyłączenie floating bubble'a (localStorage).
  const handleCloseForever = () => {
    setBubbleClosedPermanently(true);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STEFAN_BUBBLE_DISMISS_KEY, '1');
    }
  };

  const handleOpenPopover = () => {
    setBubbleHidden(true);
    setPopoverOpen(true);
  };

  const handleRebuild = async () => {
    if (selected.size === 0 || !organizationId) return;
    try {
      await rebuild({
        organizationId,
        sections: Array.from(selected),
      });
      showSuccess(t('stefan.toasts.rebuilt', 'Baza wiedzy zaktualizowana'));
      setPopoverOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[StefanKbNotifier] rebuild failed', err);
      showError(t('stefan.toasts.rebuildError', 'Nie udało się zaktualizować bazy.'));
    }
  };

  // Współdzielona zawartość popovera (header + checkboxy + akcje) — używana
  // zarówno przez floating, jak i inline.
  const renderRebuildPanel = () => (
    <PopoverInner>
      <PopoverHeader>
        <Sparkles size={18} color="#0891b2" />
        <div>
          <PopoverTitle>
            {t('stefan.kbNotifier.popoverTitle', '{{agent}} chce się czegoś nauczyć', {
              agent: guestAgentName,
            })}
          </PopoverTitle>
          <PopoverHint>
            {t(
              'stefan.kbNotifier.popoverHint',
              'Zaznacz sekcje, które mam dopisać do jego bazy wiedzy.',
            )}
          </PopoverHint>
        </div>
      </PopoverHeader>

      <SectionList>
        {dirtySections.map((section) => {
          const meta = KB_SECTION_META[section];
          const Icon = meta.icon;
          return (
            <SectionRow key={section}>
              <Checkbox
                size="sm"
                isSelected={selected.has(section)}
                onValueChange={() => toggleSection(section)}
                aria-label={t(meta.labelKey, meta.labelFallback)}
              />
              <Icon />
              <SectionLabel>{t(meta.labelKey, meta.labelFallback)}</SectionLabel>
            </SectionRow>
          );
        })}
      </SectionList>

      <Footer>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="light" onPress={() => setSelected(new Set(dirtySections))}>
            {t('stefan.selectAll', 'Wszystkie')}
          </Button>
          <Button size="sm" variant="light" onPress={() => setSelected(new Set())}>
            {t('stefan.selectNone', 'Żadne')}
          </Button>
        </div>
        <Button
          color="primary"
          size="sm"
          onPress={handleRebuild}
          isLoading={isRebuilding}
          isDisabled={selected.size === 0}
        >
          {t('stefan.rebuild', 'Aktualizuj zaznaczone')}
        </Button>
      </Footer>
    </PopoverInner>
  );

  // ---- Wariant inline (rozwinięty czat) ----
  if (isInline) {
    return (
      <InlineBanner>
        <Popover
          placement="bottom"
          isOpen={popoverOpen}
          onOpenChange={setPopoverOpen}
          shouldBlockScroll={false}
          classNames={{ content: 'border border-gray-200 rounded-lg p-3' }}
        >
          <PopoverTrigger>
            <InlineHeaderBtn type="button" aria-expanded={popoverOpen}>
              <Sparkles className="inline-spark" />
              <span className="inline-ttl">
                {t('stefan.kbNotifier.bubbleTitle', '{{agent}} chce się czegoś nauczyć', {
                  agent: guestAgentName,
                })}
              </span>
              <span className="inline-cnt">{dotLabel}</span>
              <ChevronDown className="inline-chevron" />
            </InlineHeaderBtn>
          </PopoverTrigger>
          <PopoverContent>{renderRebuildPanel()}</PopoverContent>
        </Popover>
      </InlineBanner>
    );
  }

  // ---- Wariant floating (czat zamknięty) ----
  const showBubble = !bubbleClosedPermanently && !bubbleHidden && !popoverOpen;

  return (
    <>
      {/* Overlay TYLKO gdy bubble widoczny — wybija notyfikację na pierwszy
          plan. Klik chowa bubble na tę chwilę (nie trwale). */}
      {showBubble && (
        <NotifierOverlay
          aria-hidden
          onClick={() => setBubbleHidden(true)}
        />
      )}

      <Dot aria-hidden>{dotLabel}</Dot>

      {showBubble && (
        <Bubble
          role="button"
          tabIndex={0}
          onClick={handleOpenPopover}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpenPopover();
            }
          }}
        >
          <BubbleClose
            type="button"
            aria-label={t('common.close', 'Zamknij')}
            onClick={(e) => {
              e.stopPropagation();
              handleCloseForever();
            }}
          >
            <X />
          </BubbleClose>
          <BubbleHeader>
            <AlertCircle />
            <BubbleTitle>
              {t('stefan.kbNotifier.bubbleTitle', '{{agent}} chce się czegoś nauczyć', {
                agent: guestAgentName,
              })}
            </BubbleTitle>
          </BubbleHeader>
          <BubbleBody>
            {t('stefan.kbNotifier.bubbleBody', 'Mam {{count}} nowych zmian do zaktualizowania. Kliknij, żeby wybrać.', {
              count: dirtySections.length,
            })}
          </BubbleBody>
        </Bubble>
      )}

      {/* Niewidoczny anchor obok awatara — popover otwiera się od niego.
          Bez tego HeroUI Popover wymaga trigger element. */}
      <Popover
        placement="left"
        isOpen={popoverOpen}
        onOpenChange={setPopoverOpen}
        shouldBlockScroll={false}
        classNames={{
          content: 'border border-gray-200 rounded-lg p-3',
        }}
      >
        <PopoverTrigger>
          <PopoverAnchor aria-hidden />
        </PopoverTrigger>
        <PopoverContent>{renderRebuildPanel()}</PopoverContent>
      </Popover>
    </>
  );
};
